import { useEffect } from "react";
import axios from "axios";
import apiClient from "./apiClient";
import AuthService from "./AuthService";
import { isAuthEntryRequest } from "./authApiPaths";
import { isMarketplacePublicRequest } from "./marketplaceApiPaths";
import { unwrapEnvelopeData } from "./apiHelpers";
import {
  isSubscriptionBlocked,
  isSubscriptionBypassUrl,
  markSubscriptionInactive,
} from "../utils/subscriptionAccessStore";
import { validateRequestPayload } from "../utils/apiValidation";

function isMetaProviderRequest(url) {
  const path = String(url || "");
  return (
    path.includes("/FacebookOAuth/")
    || path.includes("/FacebookClientConfig/")
    || path.includes("/FacebookWebhook/")
  );
}

const AxiosInterceptorSetup = ({ children }) => {
  useEffect(() => {
    const requestId = apiClient.interceptors.request.use(
      (config) => {
        if (
          isSubscriptionBlocked() &&
          !isSubscriptionBypassUrl(config?.url)
        ) {
          return Promise.reject(new axios.CanceledError("Subscription inactive"));
        }
        
        // GUID Validation: Validate request payload contains GUID strings (spec 009)
        // Skip validation for auth entry requests (login, logout, refresh)
        if (
          !isAuthEntryRequest(config?.url)
          && !isMetaProviderRequest(config?.url)
          && !isMarketplacePublicRequest(config?.url)
          && config?.data
        ) {
          try {
            // Auto-detect and validate GUID fields in request payload
            // This catches any accidentally sent integer IDs before they reach the backend
            validateRequestPayload(config.data, {
              skipFields: [
                'password',
                'email',
                'phone',
                'name',
                'appId',
                'leadGenId',
                'facebookPageId',
                'pageId',
                'formId',
                'adId',
                'adsetId',
                'adAccountId',
                'metaLeadId',
                'campaignId',
              ],
            });
          } catch (validationError) {
            console.error('[Axios Interceptor] GUID validation failed:', validationError);
            // Log the full request details for debugging
            console.error('Request URL:', config.url);
            console.error('Request payload:', config.data);
            // Reject request with clear error message
            return Promise.reject(validationError);
          }
        }
        
        return AuthService.prepareAuthenticatedRequest(config);
      },
      (error) => Promise.reject(error)
    );

    const responseId = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const originalRequest = error.config;

        if (
          status === 401
          && (originalRequest?.skipAuthRefresh || isAuthEntryRequest(originalRequest?.url))
        ) {
          return Promise.reject(error);
        }

        if (status === 402) {
          markSubscriptionInactive();
          if (
            typeof window !== "undefined" &&
            !window.location.pathname.includes("/subscription-inactive")
          ) {
            window.location.assign("/subscription-inactive");
          }
          return Promise.reject(error);
        }

        if (!originalRequest || status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const response = await AuthService.refreshTokens();
          const payload = unwrapEnvelopeData(response);
          const token =
            payload?.accessToken
            ?? payload?.AccessToken
            ?? payload?.newAccessToken
            ?? payload?.NewAccessToken;
          if (!token) {
            AuthService.notifySessionTimeout("token-expired");
            return Promise.reject(error);
          }
          AuthService.setSessionFromAccessToken(token);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          originalRequest.withCredentials = true;
          return apiClient(originalRequest);
        } catch (refreshError) {
          AuthService.notifySessionTimeout("token-expired");
          return Promise.reject(refreshError);
        }
      }
    );

    return () => {
      apiClient.interceptors.request.eject(requestId);
      apiClient.interceptors.response.eject(responseId);
    };
  }, []);

  return children;
};

export default AxiosInterceptorSetup;
